package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.MarketplaceRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.firebase.uploadImageToStorage
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemCondition
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Query
import dev.gitlive.firebase.firestore.FieldPath
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

/**
 * Peer-to-peer classifieds over `marketplaceItems/{id}`.
 *
 * Split out of the former 2,753-line FirestoreRepositories.kt (2026-08-02) — same code,
 * same behaviour, one file per domain. Shared collection names and helpers live in
 * FirestoreCommon.kt.
 */

/**
 * Live [MarketplaceRepository] over Cloud Firestore (GitLive). Active-only public stream (rules also
 * enforce it); status transitions are seller-only merge writes.
 */
class FirestoreMarketplaceRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : MarketplaceRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override val currentUid: String? get() = auth.currentUser?.takeIf { !it.isAnonymous }?.uid

    override fun observeActiveItems(metroId: String?): Flow<DataResult<List<MarketplaceItem>>> {
        var query: Query = firestore.collection(MARKETPLACE).where { "status" equalTo "active" }
        if (metroId != null) query = query.where { "metroId" equalTo metroId }
        return query.orderBy("createdAt", Direction.DESCENDING).limit(100).snapshots
            .map { snap ->
                val items = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<MarketplaceItemDoc>().toModel(doc.id) }.getOrNull() }
                    .sortedByDescending { it.createdAtMillis ?: 0L }
                DataResult.Success(items) as DataResult<List<MarketplaceItem>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeMyItems(): Flow<DataResult<List<MarketplaceItem>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Success(emptyList<MarketplaceItem>()) as DataResult<List<MarketplaceItem>>)
            } else {
                firestore.collection(MARKETPLACE).where { "sellerUid" equalTo user.uid }.snapshots
                    .map { snap ->
                        val items = snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<MarketplaceItemDoc>().toModel(doc.id) }.getOrNull() }
                            .sortedByDescending { it.createdAtMillis ?: 0L }
                        DataResult.Success(items) as DataResult<List<MarketplaceItem>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun getItem(itemId: String): DataResult<MarketplaceItem> = try {
        val snapshot = firestore.collection(MARKETPLACE).document(itemId).get()
        if (!snapshot.exists) DataResult.Failure(AppError.NotFound())
        else DataResult.Success(snapshot.decode<MarketplaceItemDoc>().toModel(snapshot.id))
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun getItems(ids: Collection<String>): List<MarketplaceItem> = coroutineScope {
        // One `in` query per 10 ids instead of a point read per id: a 30-item saved set is 3
        // round-trips, not 30, and each result row is billed as one read either way. Chunks run
        // concurrently. (Firestore caps `in` at 30 values now; 10 stays well inside every SDK's
        // limit and keeps a single failed chunk from voiding the whole hydration.)
        ids.distinct().chunked(IN_QUERY_CHUNK).map { chunk ->
            async {
                runCatching {
                    firestore.collection(MARKETPLACE)
                        .where { FieldPath.documentId inArray chunk }
                        .get()
                        .documents
                        .mapNotNull { doc -> runCatching { doc.decode<MarketplaceItemDoc>().toModel(doc.id) }.getOrNull() }
                }.getOrElse { emptyList() }
            }
        }.awaitAll().flatten()
    }
            .sortedByDescending { it.createdAtMillis ?: 0L }

    override suspend fun getActiveItemsBySeller(
        sellerUid: String,
        excludeItemId: String,
        limit: Int,
    ): DataResult<List<MarketplaceItem>> = try {
        // Server-side sellerUid+status+createdAt DESC (composite index in firebase/firestore.indexes.json).
        val snapshot = firestore.collection(MARKETPLACE)
            .where { "sellerUid" equalTo sellerUid }
            .where { "status" equalTo "active" }
            .orderBy("createdAt", Direction.DESCENDING)
            .limit(limit)
            .get()
        val items = snapshot.documents
            .mapNotNull { doc -> runCatching { doc.decode<MarketplaceItemDoc>().toModel(doc.id) }.getOrNull() }
            .filter { it.id != excludeItemId }
        DataResult.Success(items)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun createItem(
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        metroId: String?,
        city: String,
        photoUrls: List<String>,
    ): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (title.isBlank()) return DataResult.Failure(AppError.Unknown())
        return try {
            val ref = firestore.collection(MARKETPLACE).add(
                MarketplaceItemCreateDoc(
                    sellerUid = user.uid,
                    sellerName = user.publicName,
                    title = title.trim(),
                    description = description.trim(),
                    priceCents = if (isFree) null else priceCents,
                    isFree = isFree,
                    condition = when (condition) {
                        ItemCondition.New -> "new"
                        ItemCondition.LikeNew -> "like_new"
                        ItemCondition.Used -> "used"
                    },
                    category = category.wire,
                    metroId = metroId,
                    city = city.trim(),
                    photoUrls = photoUrls.filter { it.isNotBlank() }.take(8),
                    status = "active",
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            DataResult.Success(ref.id)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun updateItem(
        itemId: String,
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        city: String,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (title.isBlank()) return DataResult.Failure(AppError.Unknown())
        return try {
            firestore.collection(MARKETPLACE).document(itemId).set(
                MarketplaceItemUpdateDoc(
                    title = title.trim(),
                    description = description.trim(),
                    priceCents = if (isFree) null else priceCents,
                    isFree = isFree,
                    condition = when (condition) {
                        ItemCondition.New -> "new"
                        ItemCondition.LikeNew -> "like_new"
                        ItemCondition.Used -> "used"
                    },
                    category = category.wire,
                    city = city.trim(),
                ),
                merge = true,
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun markSold(itemId: String): DataResult<Unit> = try {
        firestore.collection(MARKETPLACE).document(itemId).update("status" to "sold")
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun deleteItem(itemId: String): DataResult<Unit> = try {
        // Doc delete only — the item's Storage photos are cleaned up by a lifecycle rule / are
        // orphaned harmlessly (same as posts). Rules restrict this to the owner/admin.
        firestore.collection(MARKETPLACE).document(itemId).delete()
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun addPhoto(itemId: String, image: PickedImage): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val ext = if (image.mimeType.contains("png", ignoreCase = true)) "png" else "jpg"
            val name = kotlin.random.Random.nextLong().toString().replace("-", "") + "." + ext
            // Storage path segment is "marketplace" (see firebase/storage.rules) — NOT the Firestore
            // collection name "marketplaceItems". The rule looks up the marketplaceItems doc to authorize.
            val url = uploadImageToStorage("$MARKETPLACE_STORAGE/$itemId/$name", image.bytes, image.mimeType)
            // Concurrent-safe append (two parallel uploads must not lose each other); the pre-read
            // only enforces the 8-photo cap, so a race can briefly exceed it — harmless vs lost photos.
            val current = (getItem(itemId) as? DataResult.Success)?.data?.photoUrls.orEmpty()
            if (current.size >= 8) return DataResult.Failure(AppError.Unknown())
            firestore.collection(MARKETPLACE).document(itemId)
                .update("photoUrls" to FieldValue.arrayUnion(url))
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/** Ids per Firestore `in` query in [FirestoreMarketplaceRepository.getItems]. */
private const val IN_QUERY_CHUNK = 10
